<template>
  <div class="p-1">
    <h6 class="mb-1">
      {{ isCompleted ? 'Shipment Summary' : 'Creating Shipments...' }}
    </h6>
    <p
      v-if="displayTriggeredBy"
      class="text-muted mb-1"
    >
      <small>Triggered by: <strong>{{ displayTriggeredBy }}</strong></small>
    </p>

    <div class="mb-2">
      <b-progress
        :value="processed"
        :max="totalOrders"
        :variant="progressVariant"
        animated
        class="mb-50"
      />
      <div class="d-flex justify-content-between">
        <small class="text-muted">{{ processed }} / {{ totalOrders }} processed</small>
        <small
          v-if="!isCompleted"
          class="text-muted"
        >
          <b-spinner
            small
            class="mr-25"
          />
          Processing...
        </small>
      </div>
    </div>

    <div class="d-flex mb-2">
      <div class="flex-fill text-center">
        <h4 class="text-success mb-0">
          {{ succeeded }}
        </h4>
        <small class="text-muted">Succeeded</small>
      </div>
      <div class="flex-fill text-center">
        <h4 class="text-danger mb-0">
          {{ failed }}
        </h4>
        <small class="text-muted">Failed</small>
      </div>
      <div class="flex-fill text-center">
        <h4 class="text-warning mb-0">
          {{ skipped }}
        </h4>
        <small class="text-muted">Skipped</small>
      </div>
    </div>

    <template v-if="isCompleted && failedResults.length > 0">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h6 class="mb-0">
          Failed Shipments
        </h6>
        <b-button
          size="sm"
          variant="outline-danger"
          :disabled="isRetrying"
          @click="retryAllFailed"
        >
          <b-spinner
            v-if="isRetrying"
            small
            class="mr-25"
          />
          Retry All Failed
        </b-button>
      </div>

      <b-table
        :items="failedResults"
        :fields="failedTableFields"
        responsive
        small
        class="mb-0"
      >
        <template #cell(orderSeqId)="data">
          {{ data.item.orderSeqId }}
        </template>
        <template #cell(retailerName)="data">
          {{ data.item.retailerName }}
        </template>
        <template #cell(reason)="data">
          <small class="text-danger">{{ data.item.reason }}</small>
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="flat-danger"
            :disabled="isRetrying"
            @click="retrySingle(data.item.orderId)"
          >
            Retry
          </b-button>
        </template>
      </b-table>
    </template>

    <template v-if="isCompleted && skippedResults.length > 0">
      <h6 class="mt-2 mb-1">
        Skipped Orders
      </h6>
      <b-table
        :items="skippedResults"
        :fields="skippedTableFields"
        responsive
        small
        class="mb-0"
      >
        <template #cell(reason)="data">
          <small class="text-warning">{{ data.item.reason }}</small>
        </template>
      </b-table>
    </template>
  </div>
</template>

<script>
import {
  BProgress,
  BSpinner,
  BTable,
  BButton,
} from 'bootstrap-vue'
import store from '@/store'
import { GET_BULK_SHIPMENT_STATUS, RETRY_BULK_SHIPMENT } from '@/store/modules/shipment.module'
import { apiToastError } from '@/@core/utils/toast'

export default {
  components: {
    BProgress,
    BSpinner,
    BTable,
    BButton,
  },
  props: {
    batchId: {
      type: String,
      required: true,
    },
    batchStatus: {
      type: String,
      default: 'processing',
    },
    triggeredBy: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      pollingTimeoutId: null,
      pollRound: 0,
      totalOrders: 0,
      processed: 0,
      succeeded: 0,
      failed: 0,
      skipped: 0,
      results: [],
      isRetrying: false,
      localTriggeredBy: '',
      failedTableFields: [
        { key: 'orderSeqId', label: 'Order ID' },
        { key: 'retailerName', label: 'Retailer' },
        { key: 'reason', label: 'Reason' },
        { key: 'actions', label: '' },
      ],
      skippedTableFields: [
        { key: 'orderSeqId', label: 'Order ID' },
        { key: 'retailerName', label: 'Retailer' },
        { key: 'reason', label: 'Reason' },
      ],
    }
  },
  computed: {
    displayTriggeredBy() {
      return this.localTriggeredBy || this.triggeredBy
    },
    isCompleted() {
      return this.batchStatus === 'completed' || this.batchStatus === 'failed'
    },
    progressVariant() {
      if (this.isCompleted && this.failed > 0) return 'warning'
      if (this.isCompleted) return 'success'
      return 'primary'
    },
    failedResults() {
      return this.results.filter(r => r.status === 'failed')
    },
    skippedResults() {
      return this.results.filter(r => r.status === 'skipped')
    },
  },
  mounted() {
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    startPolling(options = {}) {
      const { delayFirstPoll = false } = options
      this.stopPolling()
      const scheduleNext = () => {
        if (this.isCompleted || this._isDestroyed) return
        this.pollingTimeoutId = setTimeout(() => {
          this.runPollThenSchedule(scheduleNext)
        }, 2000)
      }
      if (delayFirstPoll) {
        this.pollingTimeoutId = setTimeout(() => {
          this.runPollThenSchedule(scheduleNext)
        }, 2000)
      } else {
        this.runPollThenSchedule(scheduleNext)
      }
    },
    stopPolling() {
      if (this.pollingTimeoutId) {
        clearTimeout(this.pollingTimeoutId)
        this.pollingTimeoutId = null
      }
    },
    async runPollThenSchedule(scheduleNext) {
      let done = false
      try {
        done = await this.pollStatus()
      } finally {
        if (!done) scheduleNext()
      }
    },
    async pollStatus() {
      this.pollRound += 1
      const round = this.pollRound
      try {
        const res = await store.dispatch(GET_BULK_SHIPMENT_STATUS, this.batchId)
        if (round !== this.pollRound) return false
        const data = res?.data?.data
        if (!data) return false
        this.applyStatusData(data)
        return data.status === 'completed' || data.status === 'failed'
      } catch {
        return false
      }
    },
    applyStatusData(data) {
      this.totalOrders = data.totalOrders
      this.processed = data.processed
      this.succeeded = data.succeeded
      this.failed = data.failed
      this.skipped = data.skipped
      this.results = data.results || []
      if (data.triggeredBy) this.localTriggeredBy = data.triggeredBy
      this.$emit('status-update', {
        status: data.status,
        totalOrders: data.totalOrders,
        succeeded: data.succeeded,
        failed: data.failed,
        skipped: data.skipped,
        triggeredBy: data.triggeredBy || '',
      })
    },
    async performRetry(payload = {}) {
      this.isRetrying = true
      try {
        await store.dispatch(RETRY_BULK_SHIPMENT, {
          batchId: this.batchId,
          payload,
        })
        this.$emit('retry')
        this.startPolling({ delayFirstPoll: true })
      } catch (err) {
        apiToastError(err)
      } finally {
        this.isRetrying = false
      }
    },
    retryAllFailed() {
      return this.performRetry({})
    },
    retrySingle(orderId) {
      return this.performRetry({ orderIds: [orderId] })
    },
  },
}
</script>
